import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Loader2, AlertTriangle, Send, Mail } from 'lucide-react';

/**
 * DecisionEmailModal — Go Colors candidate-facing Selected/Rejected notification.
 *
 * Opens after a recruiter marks a candidate Selected/Rejected (when the
 * `store_decision_emails` feature is on). Fetches a server-resolved preview
 * (From = recruiter@talentcrux.ai, To = candidate, CC = store ARM optional),
 * lets HR edit the subject/body and optionally add ARM in CC, then sends.
 *
 * LFS jobs: the server answers Selected with the ARM salary / DOJ request instead
 * (`kind: 'arm_salary_request'`, To = store ARM) — the candidate is not emailed.
 */
const DecisionEmailModal = ({
  screeningId,
  candidateId,
  candidateName,
  decision,
  onClose,
  onSent,
}) => {
  const [loading, setLoading] = useState(true);
  const [blockedReason, setBlockedReason] = useState('');
  const [preview, setPreview] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [cc, setCc] = useState(''); // ARM email (optional)
  const [to, setTo] = useState(''); // only editable when the server says so (LFS salary request)
  const [sending, setSending] = useState(false);

  const base = `/api/store-decision-emails/${screeningId}/candidates/${candidateId}/decision-email`;
  const isSelected = decision === 'selected';
  const isDropped = decision === 'dropped';
  const isSalaryRequest = preview?.kind === 'arm_salary_request';
  const title = isSalaryRequest
    ? 'Send Salary & DOJ Request'
    : isSelected
      ? 'Send Selection Email'
      : isDropped
        ? 'Send Dropped Notification'
        : 'Send Rejection Email';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setBlockedReason('');
      try {
        const res = await axios.get(`${base}/preview`, { params: { decision } });
        if (cancelled) return;
        if (res.data?.blocked) {
          setBlockedReason(res.data.reason || 'This email cannot be sent.');
        } else {
          setPreview(res.data);
          setSubject(res.data.subject || '');
          setBody(res.data.body || '');
          setCc((res.data.cc || []).join(', '));
          setTo(res.data.to || '');
        }
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 422 && err.response?.data?.reason) {
          setBlockedReason(err.response.data.reason);
        } else if (status === 403) {
          setBlockedReason('This feature is not enabled for your organisation.');
        } else {
          setBlockedReason(err.response?.data?.message || 'Failed to load the email preview.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [base, decision]);

  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const emailList = (v) => v.split(',').map((e) => e.trim()).filter(Boolean);
  const ccValid = emailList(cc).every((e) => EMAIL_RE.test(e));
  const toEditable = preview?.toEditable === true;
  const toAddresses = emailList(to);
  const toValid = !toEditable || (toAddresses.length > 0 && toAddresses.every((e) => EMAIL_RE.test(e)));

  const handleSend = async () => {
    if (!subject.trim()) {
      toast.error('Subject cannot be empty.');
      return;
    }
    if (!toValid) {
      toast.error('Enter at least one valid To email address.');
      return;
    }
    if (!ccValid) {
      toast.error('Enter valid CC email addresses.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${base}/send`, {
        decision,
        subject,
        body,
        cc: cc.trim(),
        ...(toEditable && { to: toAddresses.join(', ') }),
      });
      toast.success(isSalaryRequest ? 'Salary & DOJ request sent.' : 'Decision notification sent.');
      if (onSent) await onSent();
      onClose();
    } catch (err) {
      const status = err.response?.status;
      if (status === 422 && err.response?.data?.reason) {
        setBlockedReason(err.response.data.reason);
      } else {
        toast.error(err.response?.data?.message || 'Failed to send the email.');
      }
    } finally {
      setSending(false);
    }
  };

  const recipientRow = (label, value) => (
    <div className="flex gap-2 text-xs">
      <span className="w-12 shrink-0 font-semibold text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <span className="text-gray-800 break-all">{value || '—'}</span>
    </div>
  );

  const emailInputRow = (label, value, onChange, valid, placeholder) => (
    <div className="flex items-center gap-2 text-xs">
      <span className="w-12 shrink-0 font-semibold text-gray-400 uppercase tracking-wide">
        {label}
      </span>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        className={`flex-1 rounded-lg border px-2.5 py-1.5 text-xs text-gray-900 outline-none focus:ring-1 ${
          value && !valid
            ? 'border-red-300 focus:border-red-400 focus:ring-red-400'
            : 'border-gray-200 focus:border-[#007BFF] focus:ring-[#007BFF]'
        }`}
      />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[10000] flex items-start justify-center overflow-y-auto bg-black/40 backdrop-blur-[2px] p-4 sm:items-center">
      <div className="flex w-full max-w-3xl max-h-[calc(100dvh-2rem)] flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl">
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-gray-100 bg-white px-5 py-4">
          <h2 className="flex min-w-0 items-center gap-2 text-base font-semibold text-gray-900">
            <Mail className="w-5 h-5 text-[#007BFF]" />
            <span className="truncate">{title}</span>
            {candidateName ? (
              <span className="text-gray-400 font-normal text-sm">· {candidateName}</span>
            ) : null}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 space-y-4 overflow-y-auto px-5 py-5">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 className="w-8 h-8 animate-spin text-[#007BFF]" />
              <p className="text-sm text-gray-500">Preparing the email…</p>
            </div>
          ) : blockedReason ? (
            <div className="flex items-start gap-2.5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-500" />
              <div>
                <p className="font-semibold">This email can’t be sent yet</p>
                <p className="mt-0.5">{blockedReason}</p>
              </div>
            </div>
          ) : (
            <>
              {isSalaryRequest && (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-2.5 text-xs text-blue-800">
                  LFS job: the candidate is not emailed. This goes to the store ARM by default
                  (you can change or add To addresses) to fill in Designation, Date of Joining and
                  Gross Salary. The secure form link is added when you send.
                </div>
              )}
              {(preview?.warnings || []).map((w, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-yellow-200 bg-yellow-50 p-2.5 text-xs text-yellow-800"
                >
                  {w}
                </div>
              ))}

              {/* Resolved recipients (From read-only; To editable only for the salary request; CC editable) */}
              <div className="space-y-1.5 rounded-xl border border-gray-200 bg-gray-50/60 p-3">
                {recipientRow(
                  'From',
                  `${preview?.fromName ? `${preview.fromName} · ` : ''}${preview?.from}`
                )}
                {toEditable
                  ? emailInputRow('To', to, setTo, toValid, 'Recipient email(s), comma-separated')
                  : recipientRow('To', preview?.to)}
                {decision !== 'dropped' &&
                  emailInputRow(
                    'CC',
                    cc,
                    setCc,
                    ccValid,
                    isSalaryRequest ? 'CC (optional)' : 'ARM email (optional)'
                  )}
                {decision === 'selected' && !isSalaryRequest && (
                  <p className="pt-1 text-xs text-gray-500">
                    The salary &amp; DOJ request goes to these CC addresses after this email is sent.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-900 outline-none shadow-sm focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Message</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={9}
                  className="w-full resize-y rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm leading-relaxed text-gray-900 outline-none shadow-sm focus:border-[#007BFF] focus:ring-1 focus:ring-[#007BFF]"
                />
              </div>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex shrink-0 items-center justify-end gap-3 border-t border-gray-100 bg-white px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            disabled={sending}
            className="rounded-xl px-5 py-2.5 text-sm font-semibold text-gray-600 hover:bg-gray-100 transition disabled:opacity-50"
          >
            {blockedReason ? 'Close' : 'Cancel'}
          </button>
          {!loading && !blockedReason && (
            <button
              type="button"
              onClick={handleSend}
              disabled={sending || !ccValid || !toValid}
              className="inline-flex items-center gap-2 rounded-xl bg-[#007BFF] hover:bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md hover:shadow-lg transition disabled:opacity-50"
            >
              {sending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sending ? 'Sending…' : 'Send Email'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default DecisionEmailModal;
